import React from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, Check, FolderPlus } from "lucide-react";
import { toast } from "sonner";
import {
  useRaccoglitore, chiaveVoce, aggiungiAlRaccoglitore, togliDalRaccoglitore,
} from "@/lib/raccoglitore";

/**
 * La graffetta che mette un documento nel raccoglitore.
 *
 * Sta in fondo alla riga del documento, ovunque ci sia un documento: fascicolo
 * del dipendente, cantiere, mezzo, documenti aziendali, sistemi di gestione.
 * Premuta una seconda volta lo toglie, perche' sbagliare riga capita.
 *
 * La voce e' fatta di: `tipo` (che dice al server dove cercarlo), `id`,
 * `titolo` e `gruppo` - il proprietario, che diventa la cartella dentro
 * l'archivio.
 */
export default function BottoneRaccogli({ voce, className = "h-7 w-7 p-0" }) {
  const voci = useRaccoglitore();
  if (!voce?.id) return null;
  const k = chiaveVoce(voce);
  const dentro = voci.some((v) => chiaveVoce(v) === k);

  const premi = () => {
    if (dentro) { togliDalRaccoglitore(k); return; }
    aggiungiAlRaccoglitore(voce);
    toast.success("Nel raccoglitore", { description: voce.titolo });
  };

  return (
    <Button type="button" variant="outline" size="sm" onClick={premi}
            data-testid={`raccogli-${voce.tipo}-${voce.id}`}
            title={dentro ? "Togli dal raccoglitore" : "Metti nel raccoglitore"}
            className={`${className} ${dentro ? "border-orange-400 bg-orange-50 text-orange-600" : ""}`}>
      {dentro ? <Check className="w-3.5 h-3.5" /> : <Paperclip className="w-3.5 h-3.5" />}
    </Button>
  );
}

/**
 * Lo stesso gesto su un elenco intero: «tutti gli attestati di questa
 * persona», «tutti i documenti di questo cantiere». E' il pacchetto pronto,
 * senza spuntare riga per riga.
 */
export function BottoneRaccogliTutti({ voci, etichetta = "Tutti nel raccoglitore",
                                       className = "h-8" }) {
  const dentro = useRaccoglitore();
  const elenco = (voci || []).filter((v) => v && v.id);
  if (!elenco.length) return null;
  const chiavi = new Set(dentro.map(chiaveVoce));
  const fuori = elenco.filter((v) => !chiavi.has(chiaveVoce(v)));

  const premi = () => {
    if (!fuori.length) {
      togliDalRaccoglitore(elenco.map(chiaveVoce));
      toast.message("Tolti dal raccoglitore", { description: `${elenco.length} documenti` });
      return;
    }
    const { aggiunte, gia } = aggiungiAlRaccoglitore(elenco);
    toast.success(`${aggiunte} nel raccoglitore`, {
      description: gia ? `${gia} c'erano già` : undefined,
    });
  };

  return (
    <Button type="button" variant="outline" size="sm" onClick={premi} className={className}
            data-testid="raccogli-tutti">
      {fuori.length
        ? <><FolderPlus className="w-3.5 h-3.5 mr-1" /> {etichetta} ({fuori.length})</>
        : <><Check className="w-3.5 h-3.5 mr-1" /> Già nel raccoglitore — togli</>}
    </Button>
  );
}
